export const normalizeRoutes = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.routes || data?.items || [];
};

export const normalizeRoute = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if (data._id || data.id || data.title || data.name) return data;
  return data.data || data.route || null;
};

export const emptyStage = () => ({
  _id: "",
  stage_number: "",
  event_id: "",
  stage_name: "",
  date: "",
  starting_location: "",
  ending_location: "",
  stage_description: "",
  time_start: "",
  time_end: "",
});

export const initialRouteForm = {
  title: "",
  total_distance_km: "",
  estimated_time: "",
  description: "",
  track_map_image: "",
  stages: [],
};

const toDateInput = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const getEventIdValue = (eventId) => {
  if (!eventId) return "";
  if (typeof eventId === "string") return eventId;
  return eventId._id || eventId.id || "";
};

export const mapStageToForm = (stage) => ({
  _id: stage?._id || stage?.id || "",
  stage_number:
    stage?.stage_number === 0 || stage?.stage_number
      ? String(stage.stage_number)
      : "",
  event_id: getEventIdValue(stage?.event_id),
  stage_name: stage?.stage_name || "",
  date: toDateInput(stage?.date),
  starting_location: stage?.starting_location || "",
  ending_location: stage?.ending_location || "",
  stage_description: stage?.stage_description || "",
  time_start: stage?.time_start || "",
  time_end: stage?.time_end || "",
});

export const mapRouteToForm = (route) => ({
  title: route?.title || route?.name || "",
  total_distance_km:
    route?.total_distance_km === 0 || route?.total_distance_km
      ? String(route.total_distance_km)
      : "",
  estimated_time: route?.estimated_time || "",
  description: route?.description || "",
  track_map_image: route?.track_map_image || route?.trackMapImage || "",
  stages: Array.isArray(route?.stages)
    ? route.stages.map(mapStageToForm)
    : [],
});

const isBlankStage = (stage) => {
  if (!stage) return true;
  return !(
    String(stage.stage_number || "").trim() ||
    String(stage.event_id || "").trim() ||
    String(stage.stage_name || "").trim() ||
    String(stage.date || "").trim() ||
    String(stage.starting_location || "").trim() ||
    String(stage.ending_location || "").trim() ||
    String(stage.stage_description || "").trim() ||
    String(stage.time_start || "").trim() ||
    String(stage.time_end || "").trim()
  );
};

export const getRouteApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || error?.message || fallback;

  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${message}: ${data.errors.join(", ")}`;
  }

  if (typeof data?.error === "string" && data.error) {
    return data.error;
  }

  return message;
};

export const validateRouteForm = (form) => {
  const errors = {};

  if (!form.title?.trim()) {
    errors.title = "Title is required.";
  }

  const distance = Number(form.total_distance_km);
  if (
    form.total_distance_km === "" ||
    Number.isNaN(distance) ||
    !Number.isFinite(distance) ||
    distance < 0
  ) {
    errors.total_distance_km = "Enter a valid distance in km.";
  }

  const stageErrors = {};
  const seenNumbers = new Map();
  (form.stages || []).forEach((stage, index) => {
    if (isBlankStage(stage)) return;

    const rawNumber = String(stage.stage_number ?? "").trim();
    if (!rawNumber) {
      stageErrors[index] = {
        ...(stageErrors[index] || {}),
        stage_number: "Stage number is required.",
      };
      return;
    }

    const stageNumber = Number(rawNumber);
    if (!Number.isInteger(stageNumber) || stageNumber < 0) {
      stageErrors[index] = {
        ...(stageErrors[index] || {}),
        stage_number: "Enter a valid stage number.",
      };
      return;
    }

    if (seenNumbers.has(stageNumber)) {
      const message = "Stage numbers must be unique.";
      stageErrors[index] = {
        ...(stageErrors[index] || {}),
        stage_number: message,
      };
      const firstIndex = seenNumbers.get(stageNumber);
      stageErrors[firstIndex] = {
        ...(stageErrors[firstIndex] || {}),
        stage_number: message,
      };
    } else {
      seenNumbers.set(stageNumber, index);
    }
  });

  if (Object.keys(stageErrors).length) {
    errors.stages = stageErrors;
  }

  return errors;
};

export const buildStagesPayload = (stages = [], { includeIds = false } = {}) => {
  return stages
    .filter((stage) => !isBlankStage(stage))
    .map((stage) => {
      const item = {
        stage_number: Number(stage.stage_number),
      };

      if (includeIds && stage._id) {
        item._id = stage._id;
      }

      const eventId = String(stage.event_id || "").trim();
      if (eventId) {
        item.event_id = eventId;
      } else if (includeIds) {
        item.event_id = null;
      }

      if (String(stage.stage_name || "").trim()) {
        item.stage_name = stage.stage_name.trim();
      }
      if (String(stage.date || "").trim()) {
        item.date = stage.date.trim();
      }
      if (String(stage.starting_location || "").trim()) {
        item.starting_location = stage.starting_location.trim();
      }
      if (String(stage.ending_location || "").trim()) {
        item.ending_location = stage.ending_location.trim();
      }
      if (String(stage.stage_description || "").trim()) {
        item.stage_description = stage.stage_description.trim();
      }
      if (String(stage.time_start || "").trim()) {
        item.time_start = stage.time_start.trim();
      }
      if (String(stage.time_end || "").trim()) {
        item.time_end = stage.time_end.trim();
      }

      return item;
    });
};

export const buildRoutePayload = (form, { isEdit = false } = {}) => {
  const distance = Number(form.total_distance_km);

  const payload = {
    title: form.title.trim(),
    total_distance_km: distance,
  };

  if (form.estimated_time?.trim()) {
    payload.estimated_time = form.estimated_time.trim();
  } else if (isEdit) {
    payload.estimated_time = "";
  }

  if (form.description?.trim()) {
    payload.description = form.description.trim();
  } else if (isEdit) {
    payload.description = "";
  }

  const trackMapImage = String(form.track_map_image || "").trim();
  if (trackMapImage) {
    payload.track_map_image = trackMapImage;
  } else if (isEdit) {
    payload.track_map_image = "";
  }

  const stages = buildStagesPayload(form.stages, { includeIds: isEdit });
  if (stages.length) {
    payload.stages = stages;
  }

  return payload;
};
